import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG as QRCode } from 'qrcode.react';
import Draggable from 'react-draggable';
import GoogleFontLoader from './GoogleFontLoader';
import './CardLayoutSelection.css';

const HorizontalPreview = ({ layout }) => {
  const backgroundStyle = { 
    backgroundImage: `url(${process.env.PUBLIC_URL + layout.image})` 
  };

  return (
    <div className="horizontal-preview" style={backgroundStyle} />
  );
};

const VerticalCardPreview = ({ layout, customBackground, businessInfo, nameColor, logoPosition, namePosition, qrPosition, stampAreaPosition, onPositionChange, font, stampColor, coffeeCount }) => {
  let backgroundStyle = {};
  
  if (customBackground) {
    backgroundStyle = { backgroundImage: `url(${URL.createObjectURL(customBackground)})` };
  } else if (layout && layout.image) {
    const imageUrl = `${process.env.PUBLIC_URL}${layout.image}`;
    backgroundStyle = { backgroundImage: `url(${imageUrl})` };
  }

  const cardId = React.useMemo(() => businessInfo.name.slice(0, 3).toUpperCase() + Date.now().toString().slice(-6), [businessInfo.name]);

  const onDragStop = (type) => (e, data) => {
    onPositionChange(type, { x: data.x, y: data.y });
  };

  // columns non più necessario con dimensioni fisse in CSS

  return (
    <div className="card-preview vertical" style={backgroundStyle}>
      {businessInfo.logo && (
        <Draggable bounds="parent" onStop={onDragStop('logo')} position={logoPosition}>
          <img 
            src={businessInfo.logo}
            alt="Logo" 
            className="business-logo"
            style={{ cursor: 'move', maxWidth: '60px', maxHeight: '60px' }}
          />
        </Draggable>
      )}
      <Draggable bounds="parent" onStop={onDragStop('name')} position={namePosition}>
        <h3 
          style={{ color: nameColor, cursor: 'move', fontFamily: `'${font}', sans-serif` }} 
          className="business-name"
        >
          {businessInfo.name}
        </h3>
      </Draggable>
      <Draggable bounds="parent" onStop={onDragStop('stampArea')} position={stampAreaPosition}>
        <div className="stamp-area" style={{ backgroundColor: stampColor }}>
          {[...Array(coffeeCount || 11)].map((_, index) => (
            <div key={index} className="stamp" />
          ))}
        </div>
      </Draggable>
      <Draggable bounds="parent" onStop={onDragStop('qr')} position={qrPosition}>
        <div className="qr-code" style={{ cursor: 'move' }}>
          <QRCode value={cardId} size={64} />
        </div>
      </Draggable>
    </div>
  );
};

const CardLayoutSelection = () => {
  const [selectedLayout, setSelectedLayout] = useState(null);
  const [customBackground, setCustomBackground] = useState(null);
  const [businessInfo, setBusinessInfo] = useState(null);
  const [nameColor, setNameColor] = useState('#ffffff');
  const [namePosition, setNamePosition] = useState({ x: 10, y: 50 });
  const [logoPosition, setLogoPosition] = useState({ x: 230, y: 10 });
  const [qrPosition, setQrPosition] = useState({ x: 10, y: 380 });
  const [stampAreaPosition, setStampAreaPosition] = useState({ x: 10, y: 200 });
  const [stampColor, setStampColor] = useState('#ffffff');
  const [font, setFont] = useState('Arial');
  const [coffeeCount, setCoffeeCount] = useState(11);
  const [googleFonts] = useState([
    'Poppins','Roboto','Inter','Open Sans','Lato','Montserrat','Nunito','Work Sans','Oswald','Rubik',
    'Raleway','Merriweather','Playfair Display','Source Sans 3','Noto Sans','Quicksand','Kanit','Mukta','Cabin','Bebas Neue'
  ]);
  const navigate = useNavigate();

  const predefinedLayouts = useMemo(() => [
    { id: 1, name: 'Caffè', image: '/layouts/caffenapoli.jpg' },
    { id: 2, name: 'Cappuccino', image: '/layouts/cappuccino.jpg' },
    { id: 3, name: 'Deca', image: '/layouts/Deca.jpg' },
    { id: 4, name: 'Ginseng', image: '/layouts/ginseng.jpg' },
    { id: 5, name: 'Marocchino', image: '/layouts/marocchino.jpg' },
  ], []);

  const systemFonts = [
    'Arial', 'Helvetica', 'Times New Roman', 'Courier', 'Verdana', 'Georgia', 'Palatino', 'Garamond', 'Bookman', 'Comic Sans MS', 'Trebuchet MS', 'Arial Black'
  ];

  useEffect(() => {
    const storedBusinessInfo = JSON.parse(localStorage.getItem('businessInfo'));
    if (storedBusinessInfo) {
      setBusinessInfo(storedBusinessInfo);
    } else {
      navigate('/setup/business-info');
      return;
    }

    const saved = JSON.parse(localStorage.getItem('cardDesign'));
    if (saved) {
      setSelectedLayout(saved.layout || predefinedLayouts[0]);
      setCustomBackground(saved.customBackground || null);
      setNameColor(saved.nameColor || '#ffffff');
      setNamePosition(saved.namePosition || { x: 10, y: 50 });
      setLogoPosition(saved.logoPosition || { x: 230, y: 10 });
      setQrPosition(saved.qrPosition || { x: 10, y: 380 });
      setStampAreaPosition(saved.stampAreaPosition || { x: 10, y: 200 });
      setStampColor(saved.stampColor || '#ffffff');
      setFont(saved.font || 'Poppins');
      setCoffeeCount(saved.coffeeCount || 11);
    } else {
      setSelectedLayout(predefinedLayouts[0]);
    }
  }, [navigate, predefinedLayouts]);

  const handleLayoutSelect = (layout) => {
    setSelectedLayout(layout);
    setCustomBackground(null);
  };

  const handleCustomBackgroundUpload = (e) => {
    const file = e.target.files[0];
    setCustomBackground(file);
    setSelectedLayout(null);
  };

  const handlePositionChange = (type, newPosition) => {
    switch (type) {
      case 'logo':
        setLogoPosition(newPosition);
        break;
      case 'name':
        setNamePosition(newPosition);
        break;
      case 'qr':
        setQrPosition(newPosition);
        break;
      case 'stampArea':
        setStampAreaPosition(newPosition);
        break;
      default:
        break;
    }
  };

  const handleSubmit = async () => {
    const cardDesign = {
      layout: selectedLayout,
      customBackground: customBackground,
      nameColor,
      namePosition,
      logoPosition,
      qrPosition,
      stampAreaPosition,
      stampColor,
      font,
      coffeeCount
    };
    localStorage.setItem('cardDesign', JSON.stringify(cardDesign));
    // Salva bozza su backend (opzionale) o passa a dashboard
    navigate('/dashboard');
  };

  if (!businessInfo) {
    return <div>Caricamento...</div>;
  }

  return (
    <>
      <GoogleFontLoader font={font} />
      <div className="card-layout-selection">
        <div className="step-header">
          <span className="badge">Step 2</span>
          <h2>Personalizza il layout della tua card</h2>
        </div>
        <div className="layout-options">
          <div className="predefined-layouts">
            {predefinedLayouts.map(layout => (
              <div
                key={layout.id}
                className={`layout-option ${selectedLayout?.id === layout.id ? 'selected' : ''}`}
                onClick={() => handleLayoutSelect(layout)}
              >
                <HorizontalPreview layout={layout} />
                <p>{layout.name}</p>
              </div>
            ))}
          </div>
          <div className="custom-background-upload">
            <h3>O carica il tuo sfondo personalizzato</h3>
            <input
              type="file"
              onChange={handleCustomBackgroundUpload}
              accept="image/*"
            />
          </div>
        </div>
        <div className="customization-options">
          <div className="color-picker">
            <label htmlFor="nameColor">Colore del nome:</label>
            <input
              type="color"
              id="nameColor"
              value={nameColor}
              onChange={(e) => setNameColor(e.target.value)}
            />
          </div>
          <div className="color-picker">
            <label htmlFor="stampColor">Colore sfondo bollini:</label>
            <input
              type="color"
              id="stampColor"
              value={stampColor}
              onChange={(e) => setStampColor(e.target.value)}
            />
          </div>
          <div className="font-selector">
            <label htmlFor="font">Font del nome:</label>
            <select
              id="font"
              value={font}
              onChange={(e) => setFont(e.target.value)}
            >
              <optgroup label="System Fonts">
                {systemFonts.map(f => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </optgroup>
              <optgroup label="Google Fonts">
                {googleFonts.map(f => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </optgroup>
            </select>
          </div>
          <div className="coffee-count-selector">
            <label htmlFor="coffeeCount">Numero di caffè per completare la card:</label>
            <input
              type="number"
              id="coffeeCount"
              value={coffeeCount}
              onChange={(e) => setCoffeeCount(parseInt(e.target.value, 10))}
              min="1"
              max="20"
            />
          </div>
        </div>
        <div className="preview-section">
          <h3>Anteprima della tua card</h3>
          <p>Trascina il logo, il nome dell'attività, l'area dei bollini e il QR code per posizionarli come preferisci.</p>
          <div className="preview-container" style={{ backgroundColor: '#F0EAE5' }}>
            <VerticalCardPreview 
              layout={selectedLayout}
              customBackground={customBackground}
              businessInfo={businessInfo}
              nameColor={nameColor}
              namePosition={namePosition}
              logoPosition={logoPosition}
              qrPosition={qrPosition}
              stampAreaPosition={stampAreaPosition}
              onPositionChange={handlePositionChange}
              font={font}
              stampColor={stampColor}
              coffeeCount={coffeeCount}
            />
          </div>
        </div>
        {(selectedLayout || customBackground) && (
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button onClick={() => navigate('/setup/business-info')} className="btn btn-secondary">Indietro</button>
            <button onClick={handleSubmit} className="btn btn-primary">Finalizza Card</button>
          </div>
        )}
      </div>
    </>
  );
};

export default CardLayoutSelection;